import React, { useCallback, useEffect, useState } from "react";
import styles from './HealthSystemForm.module.scss';
import PropTypes from "prop-types";
import TextField from "../textField/TextField";
import Dropdown from "../dropdown/Dropdown";
import Button from "../button/Button";
import { formatPhoneNumber } from "../../utils/stringUtils";
import { useNavigate } from "react-router-dom";

const HealthSystemForm = ({
  formData,
  onDataChange,
  onSubmit
}) => {
  const navigate = useNavigate();
  const [errors, setErrors] = useState({});
  const [isValidated, setIsValidated] = useState(false);

  const validateForm = useCallback(() => {
    const newErrors = {};

    // Validate Name
    if (!formData.name?.trim()) {
      newErrors.name = 'Health System Name is required';
    }

    // Validate Address
    if (!formData.address?.trim()) {
      newErrors.address = 'Primary Address is required';
    }

    // Validate Email
    if (!formData.email?.trim()) {
      newErrors.email = 'Email Address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    // Validate Site Phone
    const cleanPhone = formData.phone?.replace(/\D/g, '') || '';
    if (!formData.phone?.trim()) {
       newErrors.phone = 'Phone Number is required';
    } else if (!/^\d{10}$/.test(cleanPhone)) {
      newErrors.phone = 'Invalid phone number format. Must be 10 digits.';
    }


    // Validate Contact Person
    if (!formData.contactPerson?.trim()) {
      newErrors.contactPerson = 'Contact Person is required';
    }

    // Validate Designation
    if (!formData.designation?.trim()) {
      newErrors.designation = 'Designation is required';
    }

    // Validate Contact Phone
    const cleanContactPhone = formData.contactPhone?.replace(/\D/g, '') || '';
    if (!formData.contactPhone?.trim()) {
      newErrors.contactPhone = 'Phone is required';
    } else if (!/^\d{10}$/.test(cleanContactPhone)) {
      newErrors.contactPhone = 'Invalid phone number format. Must be 10 digits.';
    }

    // Validate Contact Email
    if (!formData.contactEmail?.trim()) {
      newErrors.contactEmail = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactEmail)) {
      newErrors.contactEmail = 'Invalid email format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length > 0 ? Object.keys(newErrors)[0] : null;

  }, [formData]);

  const scrollToFirstError = (field) => {
    const headerHeight = 72; 
    const element = document.querySelector(`[data-error-field="${field}"]`);
    if (element) {
      const elementTop = element.offsetTop - headerHeight; 
      window.scrollTo({
        top: elementTop,
        behavior: 'smooth',
      });
    }
  };

  const handleSubmit = () => {
    const firstErrorField = validateForm();
    if (firstErrorField) {
      setIsValidated(true);
      scrollToFirstError(firstErrorField);
      return false;
    }
    onSubmit();
  };

  useEffect(() => {
    if (isValidated) {
      validateForm();
    }
  }, [formData, isValidated, validateForm]);

  const handleChange = (field, value) => {
    let finalValue = value;
    if (field === 'phone' || field === 'contactPhone') {
      finalValue = formatPhoneNumber(value);
    }
    
    onDataChange({
      [field]: finalValue
    });
  };

  return (
    <div className={styles.form}>
      <h2>Health System Details</h2>
      
      <div className={styles.row}>
        <div className={styles.column50} data-error-field="name">
          <TextField 
            label="Health System Name"
            value={formData.name}
            onChange={(value) => handleChange('name', value)}
            error={errors.name}
          />
        </div>
        <div className={styles.column50}>
          <Dropdown 
            label="Health System Status"
            options={[
              { value: 'Active', label: 'Active' },
              { value: 'Inactive', label: 'Inactive' }
            ]}
            value={formData.isActive ? 'Active' : 'Inactive'}
            onChange={(value) => handleChange('isActive', value === 'Active')}
          />
        </div>
      </div>

      <div className={styles.row}>
        <div className={styles.column} data-error-field="address">
          <TextField 
            label="Primary Address"
            value={formData.address}
            onChange={(value) => handleChange('address', value)}
            error={errors.address}
          />
        </div>
      </div>

      <div className={styles.row}>
        <div className={styles.column50} data-error-field="email">
          <TextField 
            label="Email Address"
            value={formData.email}
            onChange={(value) => handleChange('email', value)}
            error={errors.email}
          />
        </div>
        <div className={styles.column50} data-error-field="phone">
          <TextField 
            label="Phone Number"
            value={formData.phone}
            onChange={(value) => handleChange('phone', value)}
            error={errors.phone}
          />
        </div>
      </div>

      <div className={styles.row}>
         <div className={styles.column50}>
          <TextField 
            label="Website URL (Optional)"
            value={formData.website}
            onChange={(value) => handleChange('website', value)}
          />
        </div>
      </div>
       
      <div className={styles.sectionDivider} />

      <h2>Primary Contact Details</h2>

      <div className={styles.row}>
        <div className={styles.column50} data-error-field="contactPerson">
          <TextField 
            label="Contact Person"
            value={formData.contactPerson}
            onChange={(value) => handleChange('contactPerson', value)}
            error={errors.contactPerson}
          />
        </div>
        <div className={styles.column50} data-error-field="designation">
          <TextField 
            label="Designation"
            value={formData.designation}
            onChange={(value) => handleChange('designation', value)}
            error={errors.designation}
          />
        </div>
      </div>

      <div className={styles.row}>
        <div className={styles.column50} data-error-field="contactPhone">
          <TextField 
            label="Phone"
            value={formData.contactPhone}
            onChange={(value) => handleChange('contactPhone', value)}
            error={errors.contactPhone}
          />
        </div>
        <div className={styles.column50} data-error-field="contactEmail">
          <TextField 
            label="Email"
            value={formData.contactEmail}
            onChange={(value) => handleChange('contactEmail', value)}
            error={errors.contactEmail}
          />
        </div>
      </div>

      <div className={styles.actions}>
        <Button 
          label="Cancel" 
          variant="secondary" 
          onClick={() => navigate('/health-systems')} 
        />
        <Button 
          label="Save" 
          onClick={handleSubmit} 
        />
      </div>

    </div>
  );
};

HealthSystemForm.propTypes = {
  formData: PropTypes.object.isRequired,
  onDataChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired
};

export default HealthSystemForm;
